import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { useLang } from '../i18n';

const KEY = 'vde-stagione-anteprima';

/**
 * Anteprima fuori stagione: `?stagione=1` (o il vecchio `?natale=1`) mostra il
 * blocco di stagione in home anche a luglio, e resta memorizzata — serve a far
 * vedere il Natale a un cliente in agosto senza toccare il codice. `=0` spegne.
 */
export const isSeasonPreview = () => {
  const q = new URLSearchParams(window.location.search);
  const v = q.get('stagione') ?? q.get('natale');
  if (v === '1') localStorage.setItem(KEY, '1');
  if (v === '0') {
    localStorage.removeItem(KEY);
    localStorage.removeItem('vde-xmas-preview');
  }
  // La chiave vecchia sopravvive: chi ha l'anteprima attiva non la perde.
  return localStorage.getItem(KEY) === '1' || localStorage.getItem('vde-xmas-preview') === '1';
};

/**
 * Le stagioni pubblicate. La navigazione legge da qui: oggi c'e' solo il
 * Natale, quindi la voce dice «Natale»; il giorno in cui una seconda stagione
 * entra nel registro la voce cambia nome da sola, senza toccare l'header.
 *
 * `voce` non e' mai nulla: fuori dalla finestra resta la stagione pubblicata,
 * cosi' il link non porta in un vicolo cieco e non serve nessun conto alla
 * rovescia.
 */
export const useSeasons = () => {
  const { lang } = useLang();
  const query = useQuery({
    queryKey: ['seasons', lang],
    queryFn: async () => (await api.get(`/seasons?lang=${lang}`)).data,
    staleTime: 30 * 60_000,
  });
  const items = query.data?.items || [];
  const active = query.data?.active || null;
  return { ...query, items, active, voce: active || items[0] || null, preview: isSeasonPreview() };
};

/** Una stagione intera: racconto, idee coi pezzi, referenze. */
export const useSeason = (slug) => {
  const { lang } = useLang();
  const preview = isSeasonPreview();
  const query = useQuery({
    queryKey: ['season', slug, lang],
    queryFn: async () => (await api.get(`/seasons/${slug}?lang=${lang}&limit=400`)).data,
    enabled: !!slug,
    staleTime: 10 * 60_000,
    retry: false,
  });
  return {
    ...query,
    preview,
    // «Visibile» decide solo il blocco in home: la pagina resta raggiungibile
    // tutto l'anno, chi ci arriva da un link non trova una porta chiusa.
    visible: preview || !!query.data?.active,
  };
};
